import type { Phrase } from './types';
import { expandToken } from './abbreviations';

/**
 * Full-text search over the note library.
 *
 * The live app previously filtered notes with a single literal substring match
 * of the whole query (`name.includes(q) || body.includes(q) || …`). That made
 * multi-word queries *narrower to the point of useless* — "afib rvr" only matched
 * if those words were contiguous — and produced no ranking and no idea of where a
 * note matched. This module replaces that with a real ranked index:
 *
 * - every query term must match (AND) so adding words narrows results,
 * - prefix matching so "hyperk" finds "hyperkalemia",
 * - light fuzzy matching tolerant of typos on longer words,
 * - field weighting so a title/trigger hit outranks a deep body hit,
 * - per-result snippet + matched terms so the UI can show and localize the hit.
 *
 * Parsing, note IDs, fencing and plain-text copy are untouched — this only reads
 * the already-parsed `Phrase` model.
 */

export type SearchSnippet = {
  /** Title of the section the snippet was taken from. */
  sectionTitle: string;
  /** ~150-char excerpt of plain-text body centred on the first matched term. */
  text: string;
};

export type SearchHit = {
  phrase: Phrase;
  score: number;
  /** The user's query terms, lower-cased — used to highlight matches. */
  terms: string[];
  /** Best matching section excerpt, when a body/section match exists. */
  snippet?: SearchSnippet;
};

export type IndexedDoc = {
  id: string;
  name: string;
  trigger: string;
  tags: string;
  sectionTitles: string;
  body: string;
};

export const SEARCH_FIELDS = ['name', 'trigger', 'tags', 'sectionTitles', 'body'];

/** Title/trigger hits matter most; deep body hits least. */
export const FIELD_BOOST = { name: 7, trigger: 6, sectionTitles: 3, tags: 3, body: 1 };

/** Constructor options shared by the build-time index and its browser worker. */
export function searchIndexOptions() {
  return {
    fields: SEARCH_FIELDS,
    storeFields: [] as string[],
    searchOptions: {
      boost: FIELD_BOOST,
      combineWith: 'AND' as const,
      prefix: (term: string) => term.length >= 3,
      fuzzy: (term: string) => (term.length > 4 ? 0.2 : false),
    },
  };
}

/** Split a query into lower-case alphanumeric terms (matches the index tokenizer). */
export function tokenize(query: string): string[] {
  return (query.toLowerCase().match(/[a-z0-9]+/g) ?? []).filter(Boolean);
}

export function toSearchDocument(p: Phrase): IndexedDoc {
  return {
    id: p.id,
    name: p.name,
    trigger: p.trigger,
    tags: p.tags.join(' '),
    sectionTitles: p.sections.map((s) => s.title).join(' '),
    body: p.sections.map((s) => s.body).join('\n\n'),
  };
}

/**
 * Find the best ~150-char excerpt for the given terms: the earliest term
 * occurrence, scanning sections in document order. Bodies are already
 * fence-stripped plain text, so no markdown cleanup is needed.
 */
export function bestSnippet(phrase: Phrase, terms: string[]): SearchSnippet | undefined {
  const WINDOW = 150;
  for (const section of phrase.sections) {
    const lower = section.body.toLowerCase();
    let hit = -1;
    for (const t of terms) {
      const idx = lower.indexOf(t);
      if (idx !== -1 && (hit === -1 || idx < hit)) hit = idx;
    }
    if (hit === -1) continue;

    const start = Math.max(0, hit - 50);
    const end = Math.min(section.body.length, start + WINDOW);
    let text = section.body.slice(start, end).replace(/\s+/g, ' ').trim();
    if (start > 0) text = '… ' + text;
    if (end < section.body.length) text = text + ' …';
    return { sectionTitle: section.title, text };
  }
  return undefined;
}

/** A MiniSearch query: a string, or a combinator over sub-queries. */
type Combinator = { combineWith: 'AND' | 'OR'; queries: Array<string | Combinator> };

/**
 * Expand a query into (a) a MiniSearch combinator that AND's the concepts while
 * OR'ing each concept with its synonyms, and (b) the flat set of word terms to
 * highlight (original tokens + every expansion word).
 */
export function expandQuery(query: string): { msQuery: string | Combinator; terms: string[] } | null {
  const tokens = tokenize(query);
  if (!tokens.length) return null;

  const terms = new Set<string>();
  const concepts: Array<string | Combinator> = tokens.map((tok) => {
    terms.add(tok);
    const synonyms = expandToken(tok);
    for (const syn of synonyms) for (const w of tokenize(syn)) terms.add(w);
    return synonyms.length ? { combineWith: 'OR', queries: [tok, ...synonyms] } : tok;
  });

  const msQuery: string | Combinator =
    concepts.length === 1 ? concepts[0] : { combineWith: 'AND', queries: concepts };
  return { msQuery, terms: Array.from(terms) };
}

/** Word terms to highlight for a query, including abbreviation expansions. */
export function expandedTerms(query: string): string[] {
  return expandQuery(query)?.terms ?? [];
}

/** Small, structured-clone-friendly result returned by the search worker. */
export type SearchResult = { id: string; score: number; terms: string[] };

/** Resolve worker results to live Phrase objects and matching excerpts. */
export function hydrateSearchResults(
  results: SearchResult[],
  byId: Map<string, Phrase>
): SearchHit[] {
  const hits: SearchHit[] = [];
  for (const result of results) {
    const phrase = byId.get(result.id);
    if (!phrase) continue;
    hits.push({
      phrase,
      score: result.score,
      terms: result.terms,
      snippet: bestSnippet(phrase, result.terms),
    });
  }
  return hits;
}

export type HighlightPart = { text: string; hit: boolean };

export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Split text into runs, flagging the runs that match any term. Used to render
 * highlighted snippets and in-note matches. Returns a single non-hit part when
 * there are no terms, so callers can render unconditionally.
 */
export function highlightParts(text: string, terms: string[]): HighlightPart[] {
  const cleaned = terms.filter(Boolean).map(escapeRegExp);
  if (!cleaned.length || !text) return [{ text, hit: false }];

  const re = new RegExp(`(${cleaned.join('|')})`, 'gi');
  const parts: HighlightPart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), hit: false });
    parts.push({ text: m[0], hit: true });
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++; // guard against zero-width matches
  }
  if (last < text.length) parts.push({ text: text.slice(last), hit: false });
  return parts;
}
